import * as React from "react";
import { needsFailureCallout } from "../lib/status";
import { Button } from "../primitives/button";
import { AppLink } from "../primitives/link";
import { ActionLink } from "./action-link";
import { CopyButton } from "./copy";
import { ActionsMenu, TitleCrumbs, type Crumb, type MenuAction } from "./page";
import { StageBadge, StatusPill, type StatusProps } from "./status";

export interface FailureInfo {
  /** The typed reason code, shown mono with Copy (`RecoveryLimitExceeded`). */
  reason?: string;
  /** `status.message`: at most three scrubbed log lines. */
  message?: string;
  /** `status.fix` as a sentence. */
  fix?: string;
  /** Generated error reference, `/docs/errors/<Reason>`. */
  docsHref?: string;
  /** The one button for reasons that map to a fix (Raise limit, Add credits, Widen GPU choice). */
  action?: React.ReactNode;
  /** `…/logs#L<n>` when the log has stderr lines. */
  firstErrorHref?: string;
  /** The scoped `nodus describe …` command. */
  describeCommand: string;
}

/** The one loud failure treatment (ui-spec §3.4.10): danger for Failed, warning for system-initiated stops. */
export function FailureCallout({ failed, info }: { failed: boolean; info: FailureInfo }) {
  const tone = failed
    ? "border-danger-border bg-danger-bg text-danger-fg"
    : "border-warning-border bg-warning-bg text-warning-fg";
  return (
    <div role={failed ? "alert" : "status"} data-testid="failure-callout" className={`mt-4 rounded-md border px-3 py-2.5 ${tone}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-ui font-medium">{failed ? "Failed" : "Paused"}</span>
        {info.reason ? (
          <span className="inline-flex items-center gap-1">
            <code className="font-mono text-mono">{info.reason}</code>
            <CopyButton text={info.reason} label="Copy reason" />
          </span>
        ) : null}
      </div>
      {info.message ? (
        <pre tabIndex={0} aria-label="Failure message" className="mt-2 overflow-x-auto rounded-sm bg-sunken px-3 py-2 font-mono text-mono whitespace-pre-wrap text-fg-secondary">
          {info.message}
        </pre>
      ) : null}
      {info.fix ? <p className="mt-2 text-ui text-fg-body">{info.fix}</p> : null}
      <div className="mt-2 flex flex-wrap items-center gap-4">
        {info.action}
        {info.docsHref ? (
          <ActionLink href={info.docsHref} external>
            Docs: {info.reason ?? "this error"}
          </ActionLink>
        ) : null}
        {info.firstErrorHref ? <ActionLink href={info.firstErrorHref}>Jump to first error</ActionLink> : null}
        <span className="inline-flex items-center gap-1 text-small text-fg-secondary">
          <code className="font-mono">{info.describeCommand}</code>
          <CopyButton text={info.describeCommand} label="Copy describe command" />
        </span>
      </div>
    </div>
  );
}

export interface ResourceHeaderProps {
  /** `metadata.name` exactly: what the CLI takes. */
  name: string;
  /** Muted `Section /` links before the name. */
  parents?: Crumb[];
  status?: StatusProps;
  stage?: { stage: "Alpha" | "Beta"; detail?: string };
  /** At most four items joined by ` · ` (ui-spec §3.3). */
  meta?: React.ReactNode[];
  /** Supplementary facts available without crowding the status line. */
  details?: React.ReactNode[];
  /** The most useful command for the current tab. */
  cli: string;
  /** Link to the `?sheet=cli` view with every common command. */
  allCommandsHref?: string;
  actions: MenuAction[];
  /** Exactly one labelled action for the object's state. */
  primaryAction?: React.ReactNode;
  failure?: FailureInfo;
}

/**
 * Detail header (ui-spec §3.3, dashboard theme): `Section / name` on one line with the `⋯` menu, then one status
 * line (state · up to four facts). The CLI command rides on the heading for Shift-C.
 */
export function ResourceHeader(props: ResourceHeaderProps) {
  const meta = (props.meta ?? []).slice(0, 4);
  const showFailure = props.failure && props.status && needsFailureCallout(props.status);
  return (
    <header>
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-3">
        <div className="group flex min-w-0 items-center gap-2">
          {props.parents ? <TitleCrumbs items={props.parents} /> : null}
          <h1 data-copy-cli={props.cli} className="truncate text-resource-title font-semibold text-fg-primary">
            {props.name}
          </h1>
          <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100">
            <CopyButton text={props.name} label="Copy name" />
          </span>
          {props.stage ? <StageBadge stage={props.stage.stage}>{props.stage.detail}</StageBadge> : null}
        </div>
        <div className="flex items-center gap-2">
          {props.primaryAction}
          <ActionsMenu actions={props.actions} />
        </div>
      </div>
      {props.status || meta.length ? (
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-ui text-fg-muted">
          {props.status ? <StatusPill {...props.status} /> : null}
          {meta.map((m, i) => (
            <span key={i} className={`inline-flex min-w-0 items-baseline gap-2 tabular-nums empty:hidden ${i > 0 || props.status ? "before:content-['·']" : ""}`}>{typeof m === "string" ? <span className="nd-resource-meta-text" title={m}>{m}</span> : m}</span>
          ))}
        </p>
      ) : null}
      {props.details?.some(Boolean) ? (
        <details className="mt-3 text-small text-fg-muted">
          <summary className="w-fit cursor-pointer hover:text-fg-primary">Resource details</summary>
          <div className="mt-2 grid gap-2 rounded-md border border-line bg-subtle p-3 break-words">
            {props.details.filter(Boolean).map((detail, i) => <div key={i}>{detail}</div>)}
          </div>
        </details>
      ) : null}
      {props.allCommandsHref ? (
        <AppLink href={props.allCommandsHref} className="sr-only focus:not-sr-only">
          All CLI commands
        </AppLink>
      ) : null}
      {showFailure ? <FailureCallout failed={props.status?.phase === "Failed"} info={props.failure!} /> : null}
    </header>
  );
}

/** A secondary (outline) state action such as "Run again" or "Stop workspace", at the small size. */
export function StateAction(props: React.ComponentProps<typeof Button>) {
  return <Button variant="outline" size="sm" {...props} />;
}
